import { useEffect, useState } from 'react';
import { Building2, Edit, Plus, Search, UserX } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { useTenantStore } from '../../store/useTenantStore';
import { useToastStore } from '../../store/useToastStore';
import { Card } from '../../components/Shared/Card';
import { Button } from '../../components/Shared/Button';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '../../components/Shared/Table';
import { Badge } from '../../components/Shared/Badge';
import { EmptyState } from '../../components/Shared/EmptyState';
import { Loading } from '../../components/Shared/Loading';
import { Input } from '../../components/Shared/Input';
import { ConfirmDialog } from '../../components/Shared/ConfirmDialog';
import TenantFormDialog from './components/TenantFormDialog';
import type { FacilityFlowCoreModulesTenantsDtosTenantDto } from '../../api/generated/models';

export default function TenantsPage() {
  const { t } = useTranslation(['admin', 'common']);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<FacilityFlowCoreModulesTenantsDtosTenantDto | null>(null);
  const [deactivateDialog, setDeactivateDialog] = useState<{
    isOpen: boolean;
    tenantId: string;
    tenantName: string;
  }>({ isOpen: false, tenantId: '', tenantName: '' });

  const { tenants, loading, error, fetchTenants, deactivateTenant } = useTenantStore();
  const { addToast } = useToastStore();

  useEffect(() => {
    fetchTenants().then(() => setInitialLoadComplete(true));
  }, [fetchTenants]);

  const filteredTenants = tenants.filter((tenant) => {
    if (searchTerm) {
      const matches =
        tenant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.domain?.toLowerCase().includes(searchTerm.toLowerCase());
      if (!matches) return false;
    }
    if (filterStatus === 'active' && !tenant.isActive) return false;
    if (filterStatus === 'inactive' && tenant.isActive) return false;
    return true;
  });

  const handleCreate = () => {
    setSelectedTenant(null);
    setIsFormDialogOpen(true);
  };

  const handleEdit = (tenant: FacilityFlowCoreModulesTenantsDtosTenantDto) => {
    setSelectedTenant(tenant);
    setIsFormDialogOpen(true);
  };

  const handleDeactivate = (tenant: FacilityFlowCoreModulesTenantsDtosTenantDto) => {
    setDeactivateDialog({
      isOpen: true,
      tenantId: tenant.id || '',
      tenantName: tenant.name || '',
    });
  };

  const closeDeactivateDialog = () =>
    setDeactivateDialog({ isOpen: false, tenantId: '', tenantName: '' });

  const handleConfirmDeactivate = async () => {
    try {
      await deactivateTenant(deactivateDialog.tenantId);
      addToast(t('admin:tenants.messages.deactivated'), 'success');
      closeDeactivateDialog();
    } catch {
      addToast(t('admin:tenants.errors.deactivateFailed'), 'error');
    }
  };

  const getBusinessPlanLabel = (plan: number) => {
    const labels = ['Free', 'Basic', 'Professional', 'Enterprise'];
    return labels[plan] || 'Unbekannt';
  };

  if (loading && !initialLoadComplete) {
    return <Loading />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Tenants</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Verwalten Sie alle Mandanten im System
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Neuer Tenant
        </Button>
      </div>

      {/* Filters & Search Card */}
      <Card>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Tenant suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              aria-label="Filter nach Status"
            >
              <option value="all">Alle Status</option>
              <option value="active">Aktiv</option>
              <option value="inactive">Inaktiv</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Table or States */}
      {error && initialLoadComplete ? (
        <Card>
          <EmptyState
            variant="error"
            title="Fehler beim Laden"
            description={error}
            onRetry={() => fetchTenants()}
          />
        </Card>
      ) : filteredTenants.length === 0 ? (
        <EmptyState
          icon={<Building2 className="w-12 h-12" />}
          title={
            searchTerm || filterStatus !== 'all'
              ? 'Keine Ergebnisse gefunden'
              : 'Noch keine Tenants vorhanden'
          }
          description={
            searchTerm || filterStatus !== 'all'
              ? 'Versuchen Sie es mit anderen Suchkriterien.'
              : 'Erstellen Sie Ihren ersten Tenant, um zu beginnen.'
          }
          action={
            !searchTerm && filterStatus === 'all' ? (
              <Button onClick={handleCreate}>
                <Plus className="w-4 h-4 mr-2" />
                Tenant erstellen
              </Button>
            ) : undefined
          }
        />
      ) : (
        <Card>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Name</TableHeader>
                <TableHeader>Domain</TableHeader>
                <TableHeader>Business Plan</TableHeader>
                <TableHeader>Kontaktperson</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Erstellt am</TableHeader>
                <TableHeader className="text-right">Aktionen</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTenants.map((tenant) => (
                <TableRow key={tenant.id}>
                  <TableCell>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {tenant.name || 'Unbenannt'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {tenant.domain || '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant="info">
                      {getBusinessPlanLabel(tenant.businessPlan || 0)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {tenant.contactPerson ? (
                      <div className="text-sm">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {tenant.contactPerson.firstName} {tenant.contactPerson.lastName}
                        </div>
                        <div className="text-gray-500 dark:text-gray-400">
                          {tenant.contactPerson.email}
                        </div>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-500">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={tenant.isActive ? 'success' : 'default'}>
                      {tenant.isActive ? 'Aktiv' : 'Inaktiv'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {tenant.createdAt
                      ? new Date(tenant.createdAt).toLocaleDateString('de-DE')
                      : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1 sm:gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(tenant)}
                        title={t('common:actions.edit')}
                        className="p-1 sm:p-2"
                      >
                        <Edit className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </Button>
                      {tenant.isActive && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeactivate(tenant)}
                          title={t('admin:tenants.actions.deactivate')}
                          className="p-1 sm:p-2"
                        >
                          <UserX className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Dialog */}
      <TenantFormDialog
        isOpen={isFormDialogOpen}
        onClose={() => {
          setIsFormDialogOpen(false);
          setSelectedTenant(null);
        }}
        tenant={selectedTenant}
      />

      <ConfirmDialog
        isOpen={deactivateDialog.isOpen}
        onClose={closeDeactivateDialog}
        onConfirm={handleConfirmDeactivate}
        title={t('admin:tenants.confirm.deactivate.title')}
        message={t('admin:tenants.confirm.deactivate.message', {
          name: deactivateDialog.tenantName,
        })}
        confirmText={t('admin:tenants.actions.deactivate')}
        variant="danger"
      />
    </div>
  );
}
